/**
 * When the level-intro card (HudView.intro) is on screen, how long it stays, and when a touch may dismiss it.
 *
 * The card is built with the level, under the closed cloud wipe, and used to be timed from then: by the time the
 * clouds had parted and the new level's first (slow) frames had painted it had about a second left, too short to read
 * the chapter and the level (owner report 2026-09-24). Its hold now counts only once it is really on screen: the wipe
 * gone and the level painted IntroSettleFrames times. The wipe is only watched, never waited on, so the card adds
 * nothing to a level load; and it never blocks play (it takes no touches, and any touch after IntroSkipAfterMs ends it).
 */

/** How long the card stays once on screen: time to read the chapter ribbon, the level and its one line. */
export const IntroHoldMs = 3500;
/** The same on the level that opens a chapter: its new name and art are worth a longer look. */
export const IntroChapterHoldMs = 4500;
/** A touch dismisses the card only once it has been up this long: a tap already on its way as it appears must not throw it away unread. */
export const IntroSkipAfterMs = 700;
/** The card's fade out. */
export const IntroFadeMs = 600;
/** Frames the level paints before the hold starts: the first ones stall on shader compiles and texture uploads. */
export const IntroSettleFrames = 2;
/** The longest the card waits for the cloud wipe to lift before starting its hold anyway. */
export const IntroRevealCapMs = 8000;

/** How long the intro card holds once on screen. */
export function introHoldMs(opensChapter: boolean): number { return opensChapter ? IntroChapterHoldMs : IntroHoldMs; }

/** One intro card's clock, fed a frame at a time (times in ms, performance.now()). */
export class IntroHold {
  private painted = 0;
  private shownAt = -1;
  constructor(private readonly builtAt: number) {}

  /** A frame at `now` with the cloud wipe up or not; returns true on the frame the card counts as on screen. */
  frame(now: number, wipeUp: boolean): boolean {
    if (this.shownAt >= 0) return false;
    if (wipeUp && now - this.builtAt < IntroRevealCapMs) { this.painted = 0; return false; }
    if (++this.painted < IntroSettleFrames) return false;
    this.shownAt = now;
    return true;
  }
  /** The card is on screen (its hold is running). */
  get shown(): boolean { return this.shownAt >= 0; }
  /** A touch at `now` dismisses the card. */
  canSkip(now: number): boolean { return this.shown && now - this.shownAt >= IntroSkipAfterMs; }
}
